import { useEffect, useState } from "react";
import api from "../utils/api";
import { API_ENDPOINTS } from "../utils/urls";
import Navbar from "../components/Navbar";
import BillingCard from "../components/Billing.card";
import Footer from "../components/Footer";
import FinalizeBillModal from "../components/FinaliseBill.modal";

type Variant = {
  variantId: string | null;
  variantName: string;
  price: string | number;
};

type InventoryItem = {
  itemId: string;
  name: string;
  variants?: Variant[];
  price?: any;
};

type BillItem = {
  itemId: string;
  itemName: string;
  variantId: string | null;
  variantName: string;
  price: string;
  quantity: number;
  addOn: boolean;
};

type UserData = {
  userId: string;
  storeName: string | null;
  username: string;
  address: string | null;
  GST: string | null;
  phonenumber: string | null;
};

export default function Billing() {
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [addOns, setAddOns] = useState([]);
  const [billItems, setBillItems] = useState<BillItem[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userData, setUserData] = useState<UserData>();

  const getInventoryData = async () => {
    try {
      const res = await api.get(API_ENDPOINTS.INVENTORY.GET_DATA);
      setInventoryItems(res.data.data);
      setAddOns(res.data.addOns);
      setUserData(res.data.userData);
    } catch (error) {
      console.error("Error fetching inventory data:", error);
    }
  };

  useEffect(() => {
    getInventoryData();
  }, []);

  const addToBill = (item: InventoryItem, variant?: Variant) => {
    if (variant) {
      // Handle inventory item with variant
      const existingItem = billItems.find(
        (billItem) => billItem.variantId === variant.variantId
      );

      if (existingItem) {
        const updatedItems = billItems.map((billItem) =>
          billItem.variantId === variant.variantId
            ? { ...billItem, quantity: billItem.quantity + 1 }
            : billItem
        );
        setBillItems(updatedItems);
      } else {
        const newBillItem: BillItem = {
          itemId: item.itemId,
          itemName: item.name,
          variantId: variant.variantId,
          variantName: variant.variantName,
          price: variant.price.toString(),
          quantity: 1,
          addOn: false,
        };
        setBillItems([...billItems, newBillItem]);
      }
    } else {
      // Handle add-on (no variant)
      const existingAddOn = billItems.find(
        (billItem) => billItem.itemId === item.itemId
      );
      if (existingAddOn) {
        const updatedItems = billItems.map((billItem) =>
          billItem.itemId === item.itemId && billItem.variantId === null
            ? { ...billItem, quantity: billItem.quantity + 1 }
            : billItem
        );
        setBillItems(updatedItems);
      } else {
        const newAddOnItem: BillItem = {
          itemId: item.itemId,
          itemName: item.name,
          variantId: null, // Null to differentiate add-ons
          variantName: "Add-on", // Label it as "Add-on"
          price: item.price.toString() || "0", // assuming price is in the first variant
          quantity: 1,
          addOn: true,
        };
        setBillItems([...billItems, newAddOnItem]);
      }
    }
  };

  const removeFromBill = (variantId: string | null, itemId?: string) => {
    const updatedItems = billItems.filter((billItem) => {
      // Check if the item is a variant or an add-on (no variant)
      if (variantId) {
        return billItem.variantId !== variantId; // Remove by variantId
      } else {
        return billItem.itemId !== itemId; // Remove by itemId (for items without variants)
      }
    });
    setBillItems(updatedItems);
  };

  const updateQuantity = (
    variantId: string | null,
    quantity: number,
    itemId?: string
  ) => {
    if (quantity < 1) return;

    const updatedItems = billItems.map((billItem) => {
      // Handle item with variant
      if (variantId) {
        return billItem.variantId === variantId
          ? { ...billItem, quantity }
          : billItem;
      } else {
        // Handle item without variant
        return billItem.itemId === itemId && !billItem.variantId
          ? { ...billItem, quantity }
          : billItem;
      }
    });

    setBillItems(updatedItems);
  };

  const getTotal = () => {
    return billItems.reduce((total, billItem) => {
      return total + billItem.quantity * parseFloat(billItem.price);
    }, 0);
  };

  const finalizedItems = billItems.map((item) => ({
    id: `${item.variantId || item.itemId}`,
    storeName: userData?.storeName || null,
    name: `${item.itemName} ${item.variantName} `,
    quantity: item.quantity,
    price: Number(item.price),
    total: item.quantity * Number(item.price),
    paid: false,
    note: null,
    customerId: null,
    addOn: item.addOn,
  }));

  return (
    <div>
      <div className="p-8">
        <Navbar />
        <br />
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Billing</h1>
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-green-600 text-white px-4 py-2 rounded"
          >
            Finalize Bill (₹{getTotal().toFixed(2)})
          </button>
        </div>
        {isModalOpen && (
          <FinalizeBillModal
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              getInventoryData();
              setBillItems([]);
            }}
            finalizedItems={finalizedItems}
            userData={userData!}
          />
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 md:col-span-2">
            {[...inventoryItems, ...addOns].map((item: any) => (
              <BillingCard
                key={item.itemId}
                item={item}
                // addOns={addOns}
                onClick={(item, variant) => addToBill(item, variant)}
              />
            ))}
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-2">Bill Summary</h2>
            {billItems.length === 0 ? (
              <p className="text-gray-500">No items selected.</p>
            ) : (
              <div className="space-y-4">
                {billItems.map((billItem) => (
                  <div
                    key={billItem.variantId}
                    className="flex justify-between items-center border p-3 rounded"
                  >
                    <div>
                      <h4 className="font-medium">
                        {billItem.itemName} - {billItem.variantName}
                      </h4>
                      <p className="text-sm text-gray-600">
                        ₹{billItem.price} × {billItem.quantity} = ₹
                        {(
                          parseFloat(billItem.price) * billItem.quantity
                        ).toFixed(2)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={billItem.quantity}
                        min={1}
                        className="w-16 border px-2 py-1 rounded"
                        onChange={(e) =>
                          updateQuantity(
                            billItem.variantId,
                            parseInt(e.target.value),
                            billItem.itemId
                          )
                        }
                      />
                      <button
                        className="text-red-500"
                        onClick={() =>
                          removeFromBill(billItem.variantId, billItem.itemId)
                        }
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
                <hr />
                <div className="text-right font-semibold">
                  Total: ₹{getTotal().toFixed(2)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
